import { FormControl, IconButton, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, Tooltip } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { ReactComponent as DataSourceIcon } from "assets/icons/catalog.svg";
import { ReactComponent as DocumentDownloadIcon } from "assets/icons/document-download.svg";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import Logo from "../../assets/images/taia_logo.png";
import ChatInput from "../../components/chat/ChatInput";
import ChatMessages from "../../components/chat/ChatMessages";
import { useConversation } from "../../contexts/ConversationContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import {
    DataSource,
    DocumentInDataSource,
    Model
} from "../../models/models";
import { parseError } from "../../services/http.service";
import {
    CenterContent,
    ChatCard,
    ChatContainer,
    ChatHeader,
    ChatWrapper,
    HeaderActions,
    LogoWrapper,
    SelectsWrapper,
    TopBarContainer
} from './Chat.styled';

type ChatProps = {
    dataSources: DataSource[];
    itemsByGroup: Record<string, DocumentInDataSource[]>;
    loadingGroupIds: (number | string)[];
    onLoadGroupItems: (groupId: number) => void;
};

const Chat: React.FC<ChatProps> = ({
    dataSources,
    itemsByGroup,
    loadingGroupIds,
    onLoadGroupItems,
}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const {showSnackbar} = useSnackbar();

    const {
        state,
        sendMessage,
        retryMessage,
        retryInNewChat,
        stopGeneration,
        updateModelFromUser,
        activateVariant,
        exportToPdf,
        setInitialModel
    } = useConversation();

    const {
        messages,
        isGenerating,
        answerVariants,
        selectedModelId,
        isLoading
    } = state;

    const [models, setModels] = useState<Model[]>([]);
    const [isModelsLoading, setIsModelsLoading] = useState(false);
    const modelsLoadedRef = useRef(false);

    useEffect(() => {
        if (modelsLoadedRef.current) return;

        const fetchModels = async () => {
            try {
                setIsModelsLoading(true);
                const {data} = await axios.get('/models');
                setModels(data ?? []);

                const defaultModel = (data ?? []).find((m: Model) => m.isDefault);
                if (defaultModel) {
                    setInitialModel(defaultModel.id);
                }

                modelsLoadedRef.current = true;
            } catch (error) {
                showSnackbar(parseError(error), "error");
            } finally {
                setIsModelsLoading(false);
            }
        };
        void fetchModels();
    }, [showSnackbar, setInitialModel]);

    const handleModelChange = useCallback(async (e: any) => {
        const modelId = Number(e.target.value);
        await updateModelFromUser(modelId);
    }, [updateModelFromUser]);

    return (
        <ChatContainer>
            <ChatWrapper>
                <ChatCard $isEmpty={messages?.length === 0}>
                    <TopBarContainer>
                        <SelectsWrapper>
                            <FormControl
                                sx={{
                                    m: 1,
                                    width: '100%',
                                    '@media (min-width: 760px)': {
                                        width: 265
                                    },
                                    '& .MuiInputLabel-root': {
                                        top: '-3px'
                                    }
                                }}
                            >
                                <InputLabel id="model-select-label">{t('chatInput.model')}</InputLabel>
                                <Select
                                    id="model-select"
                                    labelId="model-select-label"
                                    value={selectedModelId ?? ''}
                                    onChange={handleModelChange}
                                    disabled={isModelsLoading}
                                    input={<OutlinedInput label={t('chatInput.model')}/>}
                                    sx={{
                                        borderRadius: '16px',
                                        '.MuiSelect-select': {
                                            padding: '12px 13px',
                                        },
                                    }}
                                    renderValue={(selected) => {
                                        if (!selected) return t('chatInput.selectModel') || 'Select model';
                                        return models.find((m) => m.id === selected)?.displayName ?? '';
                                    }}
                                >
                                    {models.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            <ListItemText
                                                primary={option.displayName}
                                                secondary={t(option.description)}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                        </SelectsWrapper>
                        <HeaderActions>
                            <Tooltip title={t('chat.goToDataSources')}>
                                <IconButton
                                    onClick={() => navigate('/data-sources/list')}
                                    sx={{color: theme.palette.primary.main}}
                                >
                                    <DataSourceIcon/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('chat.downloadConversation')}>
                                <IconButton
                                    onClick={exportToPdf}
                                    sx={{color: theme.palette.primary.main}}
                                >
                                    <DocumentDownloadIcon/>
                                </IconButton>
                            </Tooltip>
                        </HeaderActions>
                    </TopBarContainer>

                    {messages?.length === 0 && (
                        <CenterContent>
                            <LogoWrapper>
                                <img src={Logo} alt="Trusted AI Assistant Logo"/>
                            </LogoWrapper>
                            <ChatHeader $isBigSpace={messages?.length === 0}>
                                {t('chat.howCanIHelp')}
                            </ChatHeader>
                        </CenterContent>
                    )}

                    {messages?.length > 0 && (
                        <ChatMessages
                            messages={messages}
                            onRetry={retryMessage}
                            onRetryNewChat={retryInNewChat}
                            canRetry={!isGenerating}
                            variants={answerVariants}
                            onActivateVariant={activateVariant}
                            canNavigateVariants={!isGenerating}
                            isGenerating={isGenerating}
                        />
                    )}

                    <ChatInput
                        onSendMessage={sendMessage}
                        onStopGeneration={stopGeneration}
                        isGenerating={isGenerating}
                        disabled={isLoading}
                        conversationId={state.conversationId}
                    />
                </ChatCard>
            </ChatWrapper>
        </ChatContainer>
    );
};


export default Chat;
